import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, List } from '@mui/material';
import SimpleBar from 'simplebar-react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { SportEvent } from '../types/SportEventTypes';
import { ClipEventMetadata } from './ClipCreationContent';
import { GoalEventItem } from './GoalEventItem';
import { RegularEventItem } from './RegularEventItem';
import { pickEventRenderer } from '../sports/registry';
import {
  formatGameTime,
  getContrastTextColor,
  getScore,
  isPeriodEvent,
} from './PlayByPlayUtils';
import { getEventPayloadDescription } from '../utils/sportEventUtils';
import { formatPeriodPrefix } from '@/shared/utils/getPeriodName';
import type { PeriodNamingMapping } from '@/features/autoIso/types/GameTypeMetadataModel';

// ============================================================================
// Shared types (also exported for use in PlayByPlayPanel)
// ============================================================================

export type TeamInfo = {
  team: 'home' | 'away' | null;
  logoPath: string;
  color: string;
};

export type EnrichedEvent = SportEvent & {
  _teamInfo?: TeamInfo;
  clipGroupCount?: number;
  _entityIds?: string[];
};

export type EventGroup = { type: 'events' | 'goal'; events: SportEvent[] };
export type PeriodGroup = { period: number; groups: EventGroup[] };

export const DEFAULT_TEAM_INFO: TeamInfo = { team: null, logoPath: '', color: '#000' };

// How close (px) to the bottom still counts as "following" the live edge.
const FOLLOW_BOTTOM_THRESHOLD_PX = 80;
const FOLLOW_SCROLL_MS = 450;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

// Same time normalisation PlayByPlayPanel sorts by (epoch ms, missing -> 0).
export const getEventTimeMs = (event: SportEvent) => (event.time ? Number(event.time) : 0);

// Stable identity across revisions: an SES UPDATE re-issues `id` but keeps
// `sourceNativeId` (the store's own key). Custom events are namespaced since
// they may carry the sourceNativeId of the SES event they came from.
export const getEventKey = (event: SportEvent) =>
  (event.payload as { type?: string } | undefined)?.type === 'custom'
    ? `custom:${event.id}`
    : `ses:${event.sourceNativeId ?? event.id}`;

// ============================================================================
// Memoized rows
// ============================================================================
// EventsList re-renders on every SES flush. These wrappers let a row skip that
// unless its own props changed, so everything passed in must be stable — the
// per-row clip handler and the goal score are derived here, not inline above.

type GameTime = { prefix: string; clock: string; full: string };
type BuildGameTime = (clock: SportEvent['clock'] | undefined) => GameTime;
type RowClipCreate = (event: SportEvent, teamInfo: TeamInfo, gameTime: string, description: string) => void;

interface EventRowProps {
  event: SportEvent;
  eventIndex: number;
  buildGameTime: BuildGameTime;
  seasonType?: string | null;
  onDebug: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onClipCreate: RowClipCreate;
  isNew: boolean;
  isAhead: boolean;
}

const EventRow = React.memo(function EventRow({
  event,
  eventIndex,
  buildGameTime,
  seasonType,
  onDebug,
  onSeekToEvent,
  onClipCreate,
  isNew,
  isAhead,
}: EventRowProps) {
  const enrichedEvent = event as EnrichedEvent;
  const teamInfo = enrichedEvent._teamInfo || DEFAULT_TEAM_INFO;
  const gameTime = buildGameTime(event.clock);
  const description = getEventPayloadDescription(event, seasonType);
  // Let the owning sport override the row renderer for this
  // event (e.g. NBA baskets -> ScoreEventItem). Falls back to
  // the generic row when the sport declares no override.
  const RowRenderer = pickEventRenderer(event) ?? RegularEventItem;

  return (
    <RowRenderer
      event={event}
      eventIndex={eventIndex}
      teamLogoPath={teamInfo.logoPath}
      teamColor={teamInfo.color}
      gameTimePrefix={gameTime.prefix}
      gameTime={gameTime.clock}
      description={description}
      isPeriodEvent={isPeriodEvent(event)}
      onDebug={onDebug}
      clipGroupCount={enrichedEvent.clipGroupCount || 0}
      onSeekToEvent={onSeekToEvent}
      onClipCreate={(e: SportEvent) => onClipCreate(e, teamInfo, gameTime.full, description)}
      isNew={isNew}
      isAhead={isAhead}
    />
  );
});

interface GoalEventRowProps extends Omit<EventRowProps, 'eventIndex'> {
  homeTeamLogoPath: string;
  awayTeamLogoPath: string;
  homeTeamPrimaryColor: string;
  awayTeamPrimaryColor: string;
}

const GoalEventRow = React.memo(function GoalEventRow({
  event,
  buildGameTime,
  seasonType,
  onDebug,
  onSeekToEvent,
  onClipCreate,
  isNew,
  isAhead,
  homeTeamLogoPath,
  awayTeamLogoPath,
  homeTeamPrimaryColor,
  awayTeamPrimaryColor,
}: GoalEventRowProps) {
  const goalEvent = event as EnrichedEvent;
  const teamInfo = goalEvent._teamInfo || DEFAULT_TEAM_INFO;
  const teamColor = teamInfo.color || '#000';
  const gameTime = buildGameTime(goalEvent.clock);
  const description = getEventPayloadDescription(goalEvent, seasonType);

  return (
    <GoalEventItem
      event={goalEvent}
      teamColor={teamColor}
      textColor={getContrastTextColor(teamColor)}
      teamLogoPath={teamInfo.logoPath}
      homeTeamLogoPath={homeTeamLogoPath}
      awayTeamLogoPath={awayTeamLogoPath}
      homeTeamPrimaryColor={homeTeamPrimaryColor}
      awayTeamPrimaryColor={awayTeamPrimaryColor}
      gameTimePrefix={gameTime.prefix}
      gameTime={gameTime.clock}
      description={description}
      score={getScore(goalEvent)}
      onDebug={onDebug}
      clipGroupCount={goalEvent.clipGroupCount || 0}
      onSeekToEvent={onSeekToEvent}
      onClipCreate={(e) => onClipCreate(e, teamInfo, gameTime.full, description)}
      isNew={isNew}
      isAhead={isAhead}
    />
  );
});

// ============================================================================
// EventsList
// ============================================================================

export interface EventsListProps {
  groupedEventsByPeriod: PeriodGroup[];
  hasEvents: boolean;
  isConnected: boolean;
  gameStatus: string;
  homeTeamLogoPath: string;
  awayTeamLogoPath: string;
  homeTeamPrimaryColor: string;
  awayTeamPrimaryColor: string;
  onDebug: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onClipCreate: (event: SportEvent, metadata: ClipEventMetadata) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  periodRefsMap: React.MutableRefObject<Map<number, HTMLDivElement>>;
  onScroll: () => void;
  /** Ids of events that just arrived live — highlighted and followed. */
  newEventIds: Set<string>;
  /** Events later than this (epoch ms) are ahead of the video and dimmed. */
  revealedUntilMs: number;
  connectionState?: 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed' | 'stale';
  onForceReconnect?: () => void;
  seasonType?: string | null;
  periodNamingMapping?: PeriodNamingMapping;
}

export const EventsList = React.memo(function EventsList({
  groupedEventsByPeriod,
  hasEvents,
  isConnected,
  gameStatus,
  homeTeamLogoPath,
  awayTeamLogoPath,
  homeTeamPrimaryColor,
  awayTeamPrimaryColor,
  onDebug,
  onSeekToEvent,
  onClipCreate,
  scrollContainerRef,
  periodRefsMap,
  onScroll,
  newEventIds,
  revealedUntilMs,
  connectionState,
  onForceReconnect,
  seasonType,
  periodNamingMapping,
}: EventsListProps) {
  const buildGameTime = useCallback<BuildGameTime>(
    (clock) => {
      const clockStr = clock?.time ? formatGameTime(clock.time) : '--:--';
      const prefix = clock?.period
        ? formatPeriodPrefix(clock.period, periodNamingMapping, seasonType)
        : '';
      const full = prefix ? `${prefix} ${clockStr}` : clockStr;
      return { prefix, clock: clockStr, full };
    },
    [periodNamingMapping, seasonType],
  );
  const showEmptyState = !hasEvents && (gameStatus !== 'live' || isConnected);

  // Follow the live edge only once the user has scrolled to the bottom.
  // Anywhere else, new events are counted into the "N new events" badge.
  const isAtBottomRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  const scrollFrameRef = useRef(0);
  const [unseenCount, setUnseenCount] = useState(0);

  // Glide to the bottom with our own easing — native `behavior: 'smooth'` is
  // short and browser-dependent, so it reads as a jump on small deltas.
  const glideToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    cancelAnimationFrame(scrollFrameRef.current);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.scrollTop = el.scrollHeight;
      return;
    }

    const from = el.scrollTop;
    const start = performance.now();
    isAutoScrollingRef.current = true;
    const step = (now: number) => {
      const t = Math.min((now - start) / FOLLOW_SCROLL_MS, 1);
      // Re-read the target each frame: the entering row is still settling.
      const to = el.scrollHeight - el.clientHeight;
      el.scrollTop = from + (to - from) * easeOutCubic(t);
      if (t < 1) {
        scrollFrameRef.current = requestAnimationFrame(step);
      } else {
        isAutoScrollingRef.current = false;
        isAtBottomRef.current = true;
      }
    };
    scrollFrameRef.current = requestAnimationFrame(step);
  }, [scrollContainerRef]);

  useEffect(() => () => cancelAnimationFrame(scrollFrameRef.current), []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    // Our own glide fires scroll events mid-flight; they aren't user intent.
    if (el && !isAutoScrollingRef.current) {
      isAtBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_BOTTOM_THRESHOLD_PX;
      if (isAtBottomRef.current) setUnseenCount(0);
    }
    onScroll();
  }, [onScroll, scrollContainerRef]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || newEventIds.size === 0) return;

    // Only count new events the current filters actually render.
    let visibleNew = 0;
    for (const { groups } of groupedEventsByPeriod) {
      for (const group of groups) {
        for (const event of group.events) {
          if (newEventIds.has(getEventKey(event))) visibleNew++;
        }
      }
    }
    if (visibleNew === 0) return;

    // A list that barely overflows has nothing to "scroll up" from.
    const barelyScrollable = el.scrollHeight - el.clientHeight < FOLLOW_BOTTOM_THRESHOLD_PX;
    if (isAtBottomRef.current || barelyScrollable) {
      glideToBottom();
    } else {
      setUnseenCount((count) => count + visibleNew);
    }
    // groupedEventsByPeriod is read, not tracked: only a new batch should act.
  }, [newEventIds, glideToBottom, scrollContainerRef]);

  const handleJumpToNewEvents = useCallback(() => {
    setUnseenCount(0);
    glideToBottom();
  }, [glideToBottom]);

  const handleClipCreate = useCallback(
    (event: SportEvent, teamInfo: TeamInfo, gameTime: string, description: string) => {
      onClipCreate(event, {
        teamLogoPath: teamInfo.logoPath,
        gameTime,
        description,
      });
    },
    [onClipCreate],
  );

  // Determine connection banner state
  const showConnectionBanner = false;
  /* const showConnectionBanner = gameStatus === 'live' && (
    !isConnected ||
    connectionState === 'reconnecting' ||
    connectionState === 'stale' ||
    connectionState === 'connecting'
  ); */

  const getConnectionMessage = () => {
    switch (connectionState) {
      case 'reconnecting':
        return 'Reconnecting to event stream...';
      case 'stale':
        return 'Connection stale, refreshing...';
      case 'connecting':
        return 'Connecting to event stream...';
      case 'failed':
        return 'Connection failed';
      default:
        return 'Connecting to event stream...';
    }
  };

  const getBannerColor = () => {
    switch (connectionState) {
      case 'stale':
      case 'failed':
        return '#FF5722';
      case 'reconnecting':
        return '#FF9800';
      default:
        return '#FFC107';
    }
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <SimpleBar
      style={{ flex: 1, minHeight: 0 }}
      scrollableNodeProps={{
        ref: (node: HTMLDivElement) => {
          scrollContainerRef.current = node;
        },
        onScroll: handleScroll,
      }}
    >
      {showConnectionBanner && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            color: '#000',
            background: getBannerColor(),
            py: 0.5,
            px: 1,
            fontSize: '12px',
            fontFamily: 'Sohne',
            fontWeight: 500,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <span>{getConnectionMessage()}</span>
          {onForceReconnect && (
            <Button
              size="small"
              variant="text"
              onClick={onForceReconnect}
              sx={{
                minWidth: 'auto',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#000',
                textTransform: 'none',
                '&:hover': {
                  background: 'rgba(0,0,0,0.1)',
                },
              }}
            >
              Refresh
            </Button>
          )}
        </Box>
      )}

      {showEmptyState && (
        <Box sx={{ p: 2, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
          No events found
        </Box>
      )}

      {groupedEventsByPeriod.map(({ period, groups }) => (
        <Box
          key={period}
          ref={(el) => {
            if (el) {
              periodRefsMap.current.set(period, el);
            } else {
              periodRefsMap.current.delete(period);
            }
          }}
        >
          {groups.map((group, groupIndex) => {
            if (group.type === 'goal') {
              const goalEvent = group.events[0];

              return (
                <GoalEventRow
                  key={groupIndex}
                  event={goalEvent}
                  buildGameTime={buildGameTime}
                  seasonType={seasonType}
                  homeTeamLogoPath={homeTeamLogoPath}
                  awayTeamLogoPath={awayTeamLogoPath}
                  homeTeamPrimaryColor={homeTeamPrimaryColor}
                  awayTeamPrimaryColor={awayTeamPrimaryColor}
                  onDebug={onDebug}
                  onSeekToEvent={onSeekToEvent}
                  onClipCreate={handleClipCreate}
                  isNew={newEventIds.has(getEventKey(goalEvent))}
                  isAhead={getEventTimeMs(goalEvent) > revealedUntilMs}
                />
              );
            }

            return (
              <List key={groupIndex} sx={{ py: 0 }}>
                {group.events.map((event, eventIndex) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    eventIndex={eventIndex}
                    buildGameTime={buildGameTime}
                    seasonType={seasonType}
                    onDebug={onDebug}
                    onSeekToEvent={onSeekToEvent}
                    onClipCreate={handleClipCreate}
                    isNew={newEventIds.has(getEventKey(event))}
                    isAhead={getEventTimeMs(event) > revealedUntilMs}
                  />
                ))}
              </List>
            );
          })}
        </Box>
      ))}
    </SimpleBar>

    {unseenCount > 0 && (
      <Button
        size="small"
        variant="contained"
        startIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
        onClick={handleJumpToNewEvents}
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          borderRadius: 999,
          px: 1.5,
          py: 0.25,
          fontFamily: 'Sohne',
          fontSize: '12px',
          fontWeight: 500,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          backgroundColor: '#123be2',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          '&:hover': { backgroundColor: '#0f32c4' },
          '@keyframes pbpNewEventsBadgeIn': {
            from: { opacity: 0, transform: 'translate(-50%, 8px)' },
          },
          animation: 'pbpNewEventsBadgeIn 200ms ease-out',
        }}
      >
        {unseenCount} new {unseenCount === 1 ? 'event' : 'events'}
      </Button>
    )}
    </Box>
  );
});
EventsList.displayName = 'EventsList';

export default EventsList;
